import React from 'react';
import {
    ActionBarMorePrimitive,
    ActionBarPrimitive,
    AuiIf,
    ComposerPrimitive,
    ErrorPrimitive,
    MessagePrimitive,
    SuggestionPrimitive,
    ThreadPrimitive,
    useAuiState,
} from '@assistant-ui/react';
import {
    AlertCircleIcon,
    ArrowDownIcon,
    ArrowUpIcon,
    CheckIcon,
    CopyIcon,
    DownloadIcon,
    MoreHorizontalIcon,
    SquareIcon,
} from 'lucide-react';
import type { FC } from 'react';
import { MarkdownText } from '@/Components/ai-ui/markdown-text';
import { ToolFallback } from '@/Components/ai-ui/tool-fallback';
import { TooltipIconButton } from '@/Components/ai-ui/tooltip-icon-button';
import { Reasoning, ReasoningGroup } from '@/Components/ai-ui/reasoning';
import { Button } from '@/Components/shadcn/button';
import { cn } from '@/lib/utils';

export const SESSION_ERROR_MESSAGE_ID = '__session_error__';

export const Thread: FC<{ disableInput?: boolean }> = ({ disableInput }) => {
    return (
        <ThreadPrimitive.Root
            className="aui-root aui-thread-root bg-background @container flex h-full flex-col"
            style={{
                ['--composer-radius' as string]: 'var(--radius-2xl)',
                ['--composer-padding' as string]: 'calc(var(--spacing) * 2.5)',
            }}
        >
            <ThreadPrimitive.Viewport
                scrollToBottomOnInitialize
                data-slot="aui_thread-viewport"
                className="relative flex flex-1 flex-col overflow-x-auto overflow-y-scroll scroll-smooth"
            >
                <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 pt-4">
                    <AuiIf condition={(s) => s.thread.isEmpty}>
                        <ThreadWelcome />
                    </AuiIf>
                    <div data-slot="aui_message-group" className="mb-10 flex flex-col gap-y-8 empty:hidden">
                        <ThreadPrimitive.Messages>{() => <ThreadMessage />}</ThreadPrimitive.Messages>
                    </div>
                    <ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer bg-background sticky bottom-0 mt-auto flex flex-col gap-4 overflow-visible rounded-t-(--composer-radius) pb-4 md:pb-6">
                        <ThreadScrollToBottom />

                        {disableInput ? (
                            <p className="text-muted-foreground py-3 text-center text-sm">
                                This search session has ended.
                            </p>
                        ) : (
                            <Composer />
                        )}
                    </ThreadPrimitive.ViewportFooter>
                </div>
            </ThreadPrimitive.Viewport>
        </ThreadPrimitive.Root>
    );
};

const ThreadMessage: FC = () => {
    const role = useAuiState((s) => s.message.role);
    const isSessionError = useAuiState(
        (s) =>
            s.message.parts.length === 1 &&
            s.message.parts[0]?.type === 'text' &&
            (s.message.parts[0] as any).text === SESSION_ERROR_MESSAGE_ID,
    );
    const isLast = useAuiState((m) => m.message.isLast);
    const isRunning = useAuiState((m) => m.thread.isRunning);

    const shouldShowThinkingMessage = isLast && isRunning && !isSessionError;
    if (isSessionError) return <SessionErrorMessage />;

    if (role === 'user')
        return (
            <>
                <UserMessage />
                {shouldShowThinkingMessage && <ThinkingMessage />}
            </>
        );

    return (
        <>
            <AssistantMessage />
            {shouldShowThinkingMessage && <ThinkingMessage />}
        </>
    );
};

const ThinkingMessage: FC = () => (
    <MessagePrimitive.Root
        data-slot="aui_assistant-message-root"
        data-role="assistant"
        className="fade-in slide-in-from-bottom-1 animate-in relative duration-150"
    >
        <div data-slot="aui_assistant-message-content" className="text-foreground px-2 leading-relaxed wrap-break-word">
            <ThinkingIndicator />
        </div>
    </MessagePrimitive.Root>
);

const SessionErrorMessage: FC = () => (
    <MessagePrimitive.Root
        data-slot="aui_assistant-message-root"
        data-role="assistant"
        className="fade-in slide-in-from-bottom-1 animate-in relative duration-150"
    >
        <div className="px-2">
            <div className="border-destructive/30 bg-destructive/10 text-destructive flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                <AlertCircleIcon className="size-4 shrink-0" />
                <span>An error occurred. Please refresh the page to start a new search.</span>
            </div>
        </div>
    </MessagePrimitive.Root>
);

const ThinkingIndicator: FC = () => (
    <span className="text-muted-foreground inline-flex items-center text-sm italic">
        <span>Thinking</span>
        <span className="ml-0.5 inline-flex">
            <span className="animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1.2s' }}>
                .
            </span>
            <span className="animate-bounce" style={{ animationDelay: '200ms', animationDuration: '1.2s' }}>
                .
            </span>
            <span className="animate-bounce" style={{ animationDelay: '400ms', animationDuration: '1.2s' }}>
                .
            </span>
        </span>
    </span>
);

const ThreadScrollToBottom: FC = () => {
    return (
        <ThreadPrimitive.ScrollToBottom asChild>
            <TooltipIconButton
                tooltip="Scroll to bottom"
                variant="outline"
                className="aui-thread-scroll-to-bottom dark:border-border dark:bg-background dark:hover:bg-accent absolute -top-12 z-10 self-center rounded-full p-4 disabled:invisible"
            >
                <ArrowDownIcon />
            </TooltipIconButton>
        </ThreadPrimitive.ScrollToBottom>
    );
};

const ThreadWelcome: FC = () => {
    return (
        <div className="aui-thread-welcome-root my-auto flex grow flex-col">
            <div className="aui-thread-welcome-center flex w-full grow flex-col items-center justify-center">
                <div className="aui-thread-welcome-message flex size-full flex-col justify-center px-4">
                    <h1 className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in fill-mode-both text-2xl font-semibold duration-200">
                        Welcome to TalentMesh agentic candidate search!
                    </h1>

                    <p className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in fill-mode-both text-muted-foreground mt-2 text-xl delay-75 duration-200">
                        Tell us who you`re hiring
                    </p>
                </div>
            </div>

            <ThreadSuggestions />
        </div>
    );
};

const ThreadSuggestions: FC = () => {
    return (
        <div className="aui-thread-welcome-suggestions grid w-full gap-2 pb-4 @md:grid-cols-2">
            <ThreadPrimitive.Suggestions>{() => <ThreadSuggestionItem />}</ThreadPrimitive.Suggestions>
        </div>
    );
};

const ThreadSuggestionItem: FC = () => {
    return (
        <div className="aui-thread-welcome-suggestion-display fade-in slide-in-from-bottom-2 animate-in fill-mode-both duration-200 nth-[n+3]:hidden @md:nth-[n+3]:block">
            <SuggestionPrimitive.Trigger send asChild>
                <Button
                    variant="ghost"
                    className="aui-thread-welcome-suggestion bg-background hover:bg-muted h-auto w-full flex-wrap items-start justify-start gap-1 rounded-3xl border px-4 py-3 text-start text-sm transition-colors @md:flex-col"
                >
                    <SuggestionPrimitive.Title className="aui-thread-welcome-suggestion-text-1 font-medium" />

                    <SuggestionPrimitive.Description className="aui-thread-welcome-suggestion-text-2 text-muted-foreground empty:hidden" />
                </Button>
            </SuggestionPrimitive.Trigger>
        </div>
    );
};

const Composer: FC = () => {
    return (
        <ComposerPrimitive.Root className="aui-composer-root relative flex w-full flex-col">
            {/* <ComposerPrimitive.AttachmentDropzone asChild> */}
            <div
                data-slot="aui_composer-shell"
                className="bg-background focus-within:border-ring focus-within:ring-ring/50 data-[dragging=true]:border-ring data-[dragging=true]:bg-accent/50 flex w-full flex-col gap-2 rounded-(--composer-radius) border p-(--composer-padding) transition-shadow focus-within:ring-2 data-[dragging=true]:border-dashed"
            >
                {/* <ComposerAttachments /> */}

                <ComposerPrimitive.Input
                    placeholder="Write a message..."
                    className="aui-composer-input placeholder:text-muted-foreground/80 text max-h-32 min-h-10 w-full resize-none bg-transparent px-1.75 py-1 outline-none"
                    rows={1}
                    autoFocus
                    aria-label="Message input"
                />

                <ComposerAction />
            </div>
            {/* </ComposerPrimitive.AttachmentDropzone> */}
        </ComposerPrimitive.Root>
    );
};

const ComposerAction: FC = () => {
    return (
        <div className="aui-composer-action-wrapper relative flex items-center justify-end">
            {/* <ComposerAddAttachment /> */}

            <AuiIf condition={(s) => !s.thread.isRunning}>
                <ComposerPrimitive.Send asChild>
                    <TooltipIconButton
                        tooltip="Send message"
                        side="bottom"
                        type="button"
                        variant="default"
                        size="icon"
                        className="aui-composer-send size-8 rounded-full"
                        aria-label="Send message"
                    >
                        <ArrowUpIcon className="aui-composer-send-icon size-4" />
                    </TooltipIconButton>
                </ComposerPrimitive.Send>
            </AuiIf>

            <AuiIf condition={(s) => s.thread.isRunning}>
                <ComposerPrimitive.Cancel asChild>
                    <TooltipIconButton
                        tooltip="Stop agent response"
                        side="bottom"
                        type="button"
                        variant="default"
                        size="icon"
                        className="aui-composer-cancel size-8 rounded-full"
                        aria-label="Stop agent response"
                    >
                        <SquareIcon className="aui-composer-cancel-icon size-3 fill-current" />
                    </TooltipIconButton>
                </ComposerPrimitive.Cancel>
            </AuiIf>
        </div>
    );
};

const MessageError: FC = () => {
    return (
        <MessagePrimitive.Error>
            <ErrorPrimitive.Root className="aui-message-error-root border-destructive bg-destructive/10 text-destructive dark:bg-destructive/5 mt-2 rounded-md border p-3 text-sm dark:text-red-200">
                <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2" />
            </ErrorPrimitive.Root>
        </MessagePrimitive.Error>
    );
};

const AssistantMessage: FC = () => {
    // reserves space for action bar and compensates with `-mb` for consistent msg spacing

    // keeps hovered action bar from shifting layout (autohide doesn't support absolute positioning well)

    // for pt-[n] use -mb-[n + 6] & min-h-[n + 6] to preserve compensation

    const ACTION_BAR_PT = 'pt-1.5';

    const ACTION_BAR_HEIGHT = `-mb-7.5 min-h-7.5 ${ACTION_BAR_PT}`;

    const hasToolParts = useAuiState((s) => s.message.parts.some((p) => p.type === 'tool-call'));

    return (
        <MessagePrimitive.Root
            data-slot="aui_assistant-message-root"
            data-role="assistant"
            className="fade-in slide-in-from-bottom-1 animate-in relative duration-150"
        >
            <div
                data-slot="aui_assistant-message-content"
                className="text-foreground px-2 leading-relaxed wrap-break-word"
            >
                <MessagePrimitive.Parts
                    components={{
                        Text: MarkdownText,

                        Reasoning,

                        ReasoningGroup,

                        tools: { Fallback: ToolFallback },
                    }}
                />

                <MessageError />
            </div>

            {!hasToolParts && (
                <div
                    data-slot="aui_assistant-message-footer"
                    className={cn('ms-2 flex items-center', ACTION_BAR_HEIGHT)}
                >
                    <AssistantActionBar />
                </div>
            )}
        </MessagePrimitive.Root>
    );
};

const AssistantActionBar: FC = () => {
    return (
        <ActionBarPrimitive.Root
            hideWhenRunning
            autohide="not-last"
            className="aui-assistant-action-bar-root text-muted-foreground col-start-3 row-start-2 -ms-1 flex gap-1"
        >
            <ActionBarPrimitive.Copy asChild>
                <TooltipIconButton tooltip="Copy">
                    <AuiIf condition={(s) => s.message.isCopied}>
                        <CheckIcon />
                    </AuiIf>

                    <AuiIf condition={(s) => !s.message.isCopied}>
                        <CopyIcon />
                    </AuiIf>
                </TooltipIconButton>
            </ActionBarPrimitive.Copy>

            <ActionBarMorePrimitive.Root>
                <ActionBarMorePrimitive.Trigger asChild>
                    <TooltipIconButton tooltip="More" className="data-[state=open]:bg-accent">
                        <MoreHorizontalIcon />
                    </TooltipIconButton>
                </ActionBarMorePrimitive.Trigger>

                <ActionBarMorePrimitive.Content
                    side="bottom"
                    align="start"
                    className="aui-action-bar-more-content bg-popover text-popover-foreground z-50 min-w-32 overflow-hidden rounded-md border p-1 shadow-md"
                >
                    <ActionBarPrimitive.ExportMarkdown asChild>
                        <ActionBarMorePrimitive.Item className="aui-action-bar-more-item hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none">
                            <DownloadIcon className="size-4" />
                            Export as Markdown
                        </ActionBarMorePrimitive.Item>
                    </ActionBarPrimitive.ExportMarkdown>
                </ActionBarMorePrimitive.Content>
            </ActionBarMorePrimitive.Root>
        </ActionBarPrimitive.Root>
    );
};

const UserMessage: FC = () => {
    return (
        <MessagePrimitive.Root
            data-slot="aui_user-message-root"
            className="fade-in slide-in-from-bottom-1 animate-in grid auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 duration-150 [&:where(>*)]:col-start-2"
            data-role="user"
        >
            {/* <UserMessageAttachments /> */}

            <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
                <div className="aui-user-message-content peer bg-muted text-foreground rounded-2xl px-4 py-2.5 wrap-break-word empty:hidden">
                    <MessagePrimitive.Parts />
                </div>

                {/* <div className="aui-user-action-bar-wrapper absolute start-0 top-1/2 -translate-x-full -translate-y-1/2 pe-2 peer-empty:hidden rtl:translate-x-full">
                    <UserActionBar />
                </div> */}
            </div>
        </MessagePrimitive.Root>
    );
};
